import { type JSX, useEffect, useRef, useState } from 'react';

import { cn } from '@change/ds-utils';

import { checkReduceMotion } from '../../utils';

import { checkScrollVisibility, findScrollTargets } from './utils';

const DEFAULT_INTERVAL_SECONDS = 3;

type Props = {
	intervalSeconds?: number;
	className?: string;
	children?: React.ReactNode;
};

/**
 * Displays its children in a horizontally-scrolling list that automatically scrolls every
 * `intervalSeconds`. When the last element is scrolled into view, the auto-scrolling stops, giving
 * fully control to the user.
 *
 * If the browser has `prefers-reduced-motion` set, then no auto-scrolling happens.
 *
 * The scrollbar for this element is hidden.
 */
export function ActivityScroller({
	intervalSeconds = DEFAULT_INTERVAL_SECONDS,
	children,
	className,
	...rest
}: Props): JSX.Element {
	const [shouldAnimate, setShouldAnimate] = useState(() => !checkReduceMotion());
	const scrollerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!(scroller && shouldAnimate)) {
			return () => {};
		}

		const handleInterval = () => {
			const { next, last } = findScrollTargets(scroller);

			// Stop animating once the end is reached.
			if (last && checkScrollVisibility(scroller, last).right) {
				setShouldAnimate(false);
				return;
			}

			if (next) {
				// Note that this doesn't guarantee perfect alignment. The scroll-snap handles that.
				scroller.scroll({
					left: next.offsetLeft,
				});
			}
		};

		const intervalId = setInterval(handleInterval, intervalSeconds * 1_000);

		return () => {
			clearInterval(intervalId);
		};
	}, [intervalSeconds, shouldAnimate]);

	return (
		<div
			ref={scrollerRef}
			// Making each child scroll-snap *:snap-start simplifies the math we have to do in the scroll handler.
			// (Account for margin, etc.)
			// Fallback [&::-webkit-scrollbar]:hidden for hiding scrollbar in older browsers:
			className={cn(
				'snap-proximity overflow-x-scroll scroll-smooth whitespace-nowrap *:snap-start [&::-webkit-scrollbar]:hidden',
				className,
			)}
			style={{
				scrollbarWidth: 'none',
				msOverflowStyle: 'none',
			}}
			{...rest}
		>
			{children}
		</div>
	);
}
